import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Search, MapPin, Clock, Users, DollarSign, Filter, Loader2, FlaskConical } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResearchPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [mode, setMode] = useState('');
  const [paid, setPaid] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [applyDialog, setApplyDialog] = useState(false);
  const [motivationLetter, setMotivationLetter] = useState('');
  const [applying, setApplying] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [appliedProjectIds, setAppliedProjectIds] = useState(new Set());
  const [applicationStatusByProject, setApplicationStatusByProject] = useState({});
  const [roomByProject, setRoomByProject] = useState({});

  useEffect(() => {
    fetchProjects();
    fetchSpecialties();
  }, [specialty, mode, paid]);

  // Fetch my applications once (used to disable Apply button for already-applied projects)
  useEffect(() => {
    if (!user || user.role !== 'student') {
      setAppliedProjectIds(new Set());
      setApplicationStatusByProject({});
      setRoomByProject({});
      return;
    }
    (async () => {
      try {
        const [applicationsRes, roomsRes] = await Promise.all([
          api.get('/applications'),
          api.get('/research-rooms')
        ]);
        const apps = applicationsRes.data.applications || [];
        const rooms = roomsRes.data.rooms || [];
        setAppliedProjectIds(new Set(apps.map(a => a.project_id)));
        setApplicationStatusByProject(
          apps.reduce((acc, application) => {
            acc[application.project_id] = application.status;
            return acc;
          }, {})
        );
        setRoomByProject(
          rooms.reduce((acc, room) => {
            acc[room.project_id] = room;
            return acc;
          }, {})
        );
      } catch {
        // Non-blocking: page can still work without this
      }
    })();
  }, [user, api]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (specialty) params.append('specialty', specialty);
      if (mode) params.append('mode', mode);
      if (paid) params.append('paid', paid);
      const res = await axios.get(`${API}/projects?${params.toString()}`);
      setProjects(res.data.projects || []);
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  };

  const fetchSpecialties = async () => {
    try {
      const res = await axios.get(`${API}/specialties`);
      setSpecialties(res.data.specialties || []);
    } catch {}
  };

  const handleApply = async () => {
    if (!user) { toast.error('Please log in to apply'); return; }
    if (motivationLetter.trim().length < 100) {
      toast.error('Motivation letter must be at least 100 characters');
      return;
    }
    if (motivationLetter.length > 10000) {
      toast.error('Motivation letter must not exceed 10,000 characters');
      return;
    }
    setApplying(true);
    try {
      await api.post('/applications', { project_id: selectedProject.project_id, motivation_letter: motivationLetter });
      toast.success('Application submitted! Your application is now under verification.');
      setApplyDialog(false);
      setMotivationLetter('');
      setCharCount(0);
      // Optimistically mark as applied to instantly disable the Apply button
      setAppliedProjectIds(prev => new Set([...prev, selectedProject.project_id]));
      setApplicationStatusByProject((prev) => ({
        ...prev,
        [selectedProject.project_id]: 'submitted'
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally { setApplying(false); }
  };

  const handleMotivationChange = (e) => {
    const value = e.target.value;
    setMotivationLetter(value);
    setCharCount(value.length);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProjects();
  };

  const canEnterRoom = (projectId) => {
    const status = applicationStatusByProject[projectId];
    return Boolean(
      roomByProject[projectId] &&
      status === 'enrolled'
    );
  };

  return (
    <div className="page-container min-h-screen bg-slate-50" data-testid="research-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Research Opportunities
          </h1>
          <p className="text-base text-slate-600">Discover research projects across medical specialties.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-100 p-4 mb-6 shadow-sm">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search projects..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-10 bg-slate-50/50" data-testid="research-search-input" />
            </div>
            <Select value={specialty} onValueChange={setSpecialty}>
              <SelectTrigger className="w-full md:w-48 h-10" data-testid="specialty-filter">
                <SelectValue placeholder="Specialty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Specialties</SelectItem>
                {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="w-full md:w-36 h-10" data-testid="mode-filter">
                <SelectValue placeholder="Mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modes</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
            <Select value={paid} onValueChange={setPaid}>
              <SelectTrigger className="w-full md:w-36 h-10" data-testid="paid-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="false">Free</SelectItem>
                <SelectItem value="true">Paid</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white h-10" data-testid="search-btn">
              <Search className="h-4 w-4" />
            </Button>
          </form>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 text-[#1a5c5c] animate-spin" /></div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16">
            <FlaskConical className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-500">No projects found</p>
            <p className="text-sm text-slate-400">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => (
              <div key={project.project_id}
                className="bg-white border border-slate-100 rounded-xl p-5 md:p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:border-[#1a5c5c]/20 transition-all duration-300 border-l-4 border-l-[#1a5c5c]"
                data-testid={`project-card-${project.project_id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Badge className="bg-[#1a5c5c]/10 text-[#1a5c5c] border-0 text-xs">{project.specialty}</Badge>
                      <Badge variant="outline" className="text-xs">{project.mode}</Badge>
                      {project.paid ? (
                        <Badge className="bg-amber-50 text-amber-700 border border-amber-100 text-xs">${project.price}</Badge>
                      ) : (
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs">Free</Badge>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>{project.title}</h3>
                    <p className="text-sm text-slate-500 mb-1">by {project.mentor_name}</p>
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">{project.description}</p>
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{project.duration}</span>
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{project.slots_available - (project.slots_filled || 0)} slots left</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{project.mode}</span>
                    </div>
                  </div>
                  {canEnterRoom(project.project_id) ? (
                    <Button
                      onClick={() => navigate(`/research-room/${roomByProject[project.project_id].room_id}`)}
                      className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white shrink-0 active:scale-95 transition-transform"
                      data-testid={`enter-room-btn-${project.project_id}`}
                    >
                      Enter Room
                    </Button>
                  ) : (
                    <Button
                      onClick={() => { setSelectedProject(project); setApplyDialog(true); }}
                      className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white shrink-0 active:scale-95 transition-transform"
                      data-testid={`apply-btn-${project.project_id}`}
                      disabled={
                        !user ||
                        user.role !== 'student' ||
                        appliedProjectIds.has(project.project_id) ||
                        (project.slots_filled || 0) >= (project.slots_available || 0) ||
                        project.project_status === 'closed'
                      }
                    >
                      {appliedProjectIds.has(project.project_id)
                        ? 'Applied'
                        : ((project.slots_filled || 0) >= (project.slots_available || 0) || project.project_status === 'closed')
                          ? 'Full'
                          : 'Apply Now'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Apply Dialog */}
      <Dialog open={applyDialog} onOpenChange={setApplyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Apply to Research Project</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-900">{selectedProject.title}</p>
                <p className="text-xs text-slate-500">by {selectedProject.mentor_name} • {selectedProject.specialty}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Motivation Letter <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-slate-500 mb-2">
                  Tell us why you're interested in this project, your relevant experience, and what you hope to learn. (100-10,000 characters)
                </p>
                <Textarea 
                  placeholder="Dear research team,

I am writing to express my strong interest in participating in this research project. My background in [relevant field/experience] has prepared me well for this opportunity...

I am particularly excited about this project because...

I believe I can contribute to your team by...

Thank you for considering my application."
                  value={motivationLetter} 
                  onChange={handleMotivationChange}
                  rows={8} 
                  className={charCount < 100 ? 'border-amber-300' : charCount > 10000 ? 'border-red-300' : 'border-slate-200'}
                  data-testid="apply-motivation-letter" 
                />
                <div className="flex justify-between mt-1">
                  <span className={`text-xs ${charCount < 100 ? 'text-amber-600' : charCount > 10000 ? 'text-red-600' : 'text-slate-400'}`}>
                    {charCount < 100 ? `${100 - charCount} more characters needed` : charCount > 10000 ? `${charCount - 10000} characters over limit` : 'Good length!'}
                  </span>
                  <span className="text-xs text-slate-400">{charCount.toLocaleString()} / 10,000</span>
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> Your application will first be reviewed by PRASHA CoRe verification team before being sent to the mentor.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleApply} 
              disabled={applying || charCount < 100 || charCount > 10000} 
              className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" 
              data-testid="submit-application-btn"
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Submit Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
